import React from 'react'
import styled from 'styled-components'

import { getImage } from 'gatsby-plugin-image'
import { formatDateTime } from '../../helpers/formatDateTime'
import GetFocalRatio from '../../helpers/getFocalRatio'
import { isCurrentlyOn } from '../../helpers/is-currently-on'
import Parallax from '../../helpers/parallax'
import {
  FeaturedTag,
  H1StyledLeft,
  LeadingParagraph,
  MetaSectionBottom,
} from '../../pages/whats-on'
import { ExtendedH6 } from '../Typography/Typography'
import {
  ContentWrapper,
  ExtraMeta,
  HeroImage,
  StyledMetaInformation,
  StyledMetaInformationContainer,
} from '../atoms/style-helpers'
import theme from '../atoms/theme'
import {
  BackgroundFill,
  CategorySeparator,
  MetaSection,
  StyledArrowLink,
  renderCategories,
} from './heroCarousel'

const { colours } = theme

const StyledDate = styled.div`
  text-align: left;
  flex-shrink: 0;
`

const FeaturedHeaderStyle = styled.div`
  ${CategorySeparator} {
    color: ${colours.primary};

    &::before {
      color: ${colours.primary};
    }

    &:hover {
      color: ${colours.light};

      &::before {
        color: ${colours.primary};
      }
    }
  }

  ${MetaSection} {
    display: flex;
    flex-direction: column;
    justify-content: space-between;
    align-items: flex-start;
  }

  ${StyledArrowLink} {
    margin-bottom: 0;
  }

  ${ContentWrapper} {
    background-color: transparent;
  }
`

type FeaturedHeaderProps = {
  event: Queries.ContentfulEvent
}

const FeaturedHeader: React.FC<FeaturedHeaderProps> = ({ event }) => {
  const formattedDateTime = event
    ? event.startDate &&
      event.endDate &&
      formatDateTime(event.startDate, event.endDate)
    : null
  const heroImage = event.heroImage ? getImage(event.heroImage) : null

  return (
    <FeaturedHeaderStyle>
      <HeroImage>
        <Parallax
          image={
            getImage(event?.imageWithFocalPoint?.image) ||
            getImage(event?.heroImage)
          }
          focalRatio={GetFocalRatio(event?.imageWithFocalPoint)}
        />
      </HeroImage>
      <ContentWrapper>
        <ExtraMeta>
          <BackgroundFill>
            <StyledMetaInformationContainer>
              <StyledMetaInformation fullWidth>
                <FeaturedTag>Featured</FeaturedTag>
                <MetaSection>
                  <H1StyledLeft>
                    {event.preTitle && (
                      <>
                        {event.preTitle}
                        <br />
                      </>
                    )}
                    {event.title}
                  </H1StyledLeft>
                  <MetaSectionBottom>
                    <ExtendedH6>
                      {event.eventType && (
                        <CategorySeparator
                          to={`/${event.eventType.slug}s/`}
                          tabIndex={-1}
                        >
                          {event.eventType.title}
                        </CategorySeparator>
                      )}
                      {event.category && (
                        <CategorySeparator tabIndex={-1}>
                          {event.category.title}
                        </CategorySeparator>
                      )}
                      {event.categories &&
                        renderCategories(event.eventType, event.categories)}
                      {isCurrentlyOn(event.startDate, event.endDate) && (
                        <CategorySeparator as='span'>
                          Currently on
                        </CategorySeparator>
                      )}
                    </ExtendedH6>
                    <StyledDate>
                      {event.customDateText
                        ? event.customDateText
                        : formattedDateTime}
                    </StyledDate>
                  </MetaSectionBottom>
                </MetaSection>
                <MetaSection>
                  {event.leadingParagraph && (
                    <LeadingParagraph>
                      {event.leadingParagraph.leadingParagraph}
                    </LeadingParagraph>
                  )}
                  {event.eventType && (
                    <StyledArrowLink
                      to={`/${event.eventType.slug}s/${event.slug}/`}
                    >
                      Learn more
                    </StyledArrowLink>
                  )}
                </MetaSection>
              </StyledMetaInformation>
            </StyledMetaInformationContainer>
          </BackgroundFill>
        </ExtraMeta>
      </ContentWrapper>
    </FeaturedHeaderStyle>
  )
}

export default FeaturedHeader
